import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ScreeningInsightsResponse } from '@estercare/ester-shared'
import { theme } from '../../../../theme/theme'
import { Bold } from '../../../styles/Typography'
import { Grid } from '../../../Grid'
import { StatisticsSection } from '../StatisticsSection'
import { SeverityByConditionCard } from '../cards/SeverityByConditionCard'
import { ProductivityBySeverityCard } from '../cards/ProductivityBySeverityCard'
import { AgeDistributionCard } from '../cards/AgeDistributionCard'
import { KeyMetricTiles } from '../cards/KeyMetricTiles'
import { getConditionRows } from '../../../../utils/statistics/severityByCondition'
import { getProductivityRows } from '../../../../utils/statistics/workImpact'

type Props = {
  insights?: ScreeningInsightsResponse
  chartWidth?: number
  chartHeight?: number
  chartAnimated?: boolean
}

export const OrganisationNeedsSection = ({ insights, chartWidth, chartHeight, chartAnimated }: Props) => {
  const { t } = useTranslation()
  const { severityByCondition, severeScreenings, careNeedLevel3, ageDistribution, productivityBySeverity } =
    insights ?? {}

  const conditionRows = useMemo(() => getConditionRows(severityByCondition, t), [severityByCondition, t])
  const productivityRows = useMemo(() => getProductivityRows(productivityBySeverity, t), [productivityBySeverity, t])

  // No response means the request failed, not a small organisation — the view reports that itself.
  if (!insights) return null

  return (
    <StatisticsSection
      title={t('admin.statistics.needsTitle')}
      intro={t('admin.statistics.needsIntro')}
      introDetail={
        severeScreenings &&
        careNeedLevel3 && (
          <Trans
            i18nKey="admin.statistics.needsIntroDetail"
            values={{ severePct: Math.round(severeScreenings.pct), carePct: Math.round(careNeedLevel3.pct) }}
            components={{ bold: <Bold /> }}
          />
        )
      }
    >
      {/* The chart carries the detail; the tiles read as a summary beside it. */}
      <CardRow $template="2fr 1fr" $printTemplate="2fr 1fr">
        <SeverityByConditionCard rows={conditionRows} />
        <KeyMetricTiles severeScreenings={severeScreenings} careNeedLevel3={careNeedLevel3} />
      </CardRow>

      <CardRow $template="1fr 1fr" $printTemplate="1fr">
        <AgeDistributionCard
          data={ageDistribution}
          chartWidth={chartWidth}
          chartHeight={chartHeight}
          chartAnimated={chartAnimated}
        />
        <ProductivityBySeverityCard rows={productivityRows} />
      </CardRow>
    </StatisticsSection>
  )
}

const CardRow = styled(Grid)<{ $template: string; $printTemplate: string }>`
  grid-template-columns: ${({ $template }) => $template};
  gap: ${theme.spacing.medium}px;

  & + & {
    margin-top: ${theme.spacing.medium}px;
  }

  @media print {
    grid-template-columns: ${({ $printTemplate }) => $printTemplate};
  }

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.small}px;

    & + & {
      margin-top: ${theme.spacing.small}px;
    }
  }
`
